export default function WinterParkDestinationsSection() {
  const stats = [
    {
      value: "42%",
      label: "of Winter Park requests are Rollins College study abroad or academic-related travel",
    },
    {
      value: "3–6 weeks",
      label: "average notice for Rollins study abroad program departures",
    },
    {
      value: "Mar–Aug",
      label: "peak travel season — passport renewals spike before spring break and summer programs",
    },
    {
      value: "48-hour",
      label: "shortest notice we've accommodated for a Winter Park business traveler",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Spain & Italy",
      context: "The most common European destinations for Rollins College study abroad programs — Barcelona, Salamanca, Madrid, Florence, and Rome host semester-long language and cultural immersion programs. Winter Park families also vacation in Spain and Italy during summer breaks. These trips are often planned 2–4 months in advance but passport issues are discovered only after program acceptance or flight booking.",
      urgency: "Rollins study abroad acceptance letters typically arrive 6–8 weeks before departure — insufficient time for standard passport processing if renewal is needed",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London, Edinburgh, and Dublin draw Winter Park travelers for multiple reasons: Rollins students participate in short-term programs and Model UN conferences, professionals attend legal and medical conferences, and families take summer cultural tours. British Airways and Virgin Atlantic operate direct MCO-to-London flights, making the UK one of the most accessible European destinations from Winter Park.",
      urgency: "Conference invitations and program acceptances often arrive 4–6 weeks before travel — compressed timeline for passport renewal",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Winter Park families — whether by cruise from Port Canaveral or by direct flight from MCO to Nassau, Montego Bay, or San Juan. Disney Cruise Line, Royal Caribbean, and Norwegian operate extensively from Port Canaveral, and Winter Park families with young children are core demographics for Disney's Bahamas and Caribbean itineraries.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Costa Rica",
      context: "Costa Rica is a staple destination for Rollins College short-term field studies, environmental science programs, and winter break service trips. The country's ecological diversity makes it ideal for academic programs focused on sustainability and conservation. Winter Park families also vacation in Costa Rica for eco-tourism and adventure travel during spring break and summer.",
      urgency: "Rollins field study departures are typically announced 4–6 weeks before travel — tight window for first-time passport applicants",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "France & Germany",
      context: "Paris and Frankfurt draw Winter Park professionals for business conferences, medical conventions, and corporate meetings. Lufthansa and Air France operate direct MCO-to-Frankfurt and MCO-to-Paris flights seasonally. Rollins students also participate in French language immersion programs in Paris and southern France. The strong business and academic ties make these destinations consistently popular.",
      urgency: "Business conference invitations and professional meeting schedules often finalize 3–5 weeks before departure",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Latin America",
      context: "Cancun, Riviera Maya, and Mexico City are accessible from MCO with direct or one-stop flights through Atlanta and Houston. Winter Park families travel to Mexico for spring break beach vacations, and professionals attend corporate meetings in Mexico City. Rollins students participate in service-learning programs in Guatemala and the Dominican Republic. Mexico requires six months of passport validity for entry.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February for Winter Park families",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Winter Park Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Winter Park
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Winter Park travelers head to Europe for study abroad and cultural travel, the Caribbean for family cruises and resorts, and Latin America for academic programs and eco-tourism. These are the destinations that create the most passport urgency for our Winter Park clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout Central Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Orlando", href: "/cities/orlando" },
              { name: "Tampa", href: "/cities/tampa" },
              { name: "Jacksonville", href: "/cities/jacksonville" },
              { name: "Sarasota", href: "/cities/sarasota" },
              { name: "Clearwater", href: "/cities/clearwater" },
              { name: "St. Petersburg", href: "/cities/st-petersburg" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}